import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, FormControl, FormLabel, FormErrorMessage, InputGroup, Input, InputRightElement, IconButton, Button, Checkbox, Alert, AlertIcon, Text, Link, SimpleGrid, useToast } from '@chakra-ui/react';
import { Visibility, VisibilityOff, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { useFormik } from 'formik';

import AuthShell from '../components/AuthShell';
import GoogleButton from '../components/GoogleButton';
import PhoneInput from '../components/PhoneInput';
import RegisterValidations from '../validations/RegisterValidations';
import { Register as Signup } from '../services/AuthServices';
import { detectCountry, isValidPhoneFor, toStoredPhone } from '../validations/PhoneValidations';
import { useUserContext } from '../contexts/UserContext';

const PASSWORD_RULES = [
  { label: '8+ characters', test: (p) => p.length >= 8 },
  { label: 'A letter', test: (p) => /[A-Za-z]/.test(p) },
  { label: 'A number', test: (p) => /\d/.test(p) }
];

const Register = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { currentUser, login } = useUserContext();
  const [show, setShow] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState(detectCountry());
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [error, setError] = useState('');
  const from = location.state?.from;

  useEffect(() => {
    if (currentUser) navigate('/', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { values, errors, touched, handleSubmit, handleChange, handleBlur, isSubmitting, setSubmitting, setFieldValue } = useFormik({
    initialValues: { firstName: '', lastName: '', email: '', password: '', terms: false },
    validationSchema: RegisterValidations,
    onSubmit: async (values) => {
      setError('');
      if (!isValidPhoneFor(phoneCountry, phone)) {
        setPhoneError(phoneCountry === 'IN' ? 'Enter a valid 10-digit mobile number.' : 'Enter a valid mobile number.');
        setSubmitting(false);
        return;
      }
      try {
        const result = await Signup(values.firstName.trim(), values.lastName.trim(), values.email.trim(), values.password, toStoredPhone(phoneCountry, phone));
        const user = result.data.newUser;
        // Log the new customer straight in
        login(user, result.data.token, true);
        toast({ title: `Welcome to Raano, ${user.firstName}!`, description: 'Your account is ready.', status: 'success', duration: 2500, position: 'top' });
        navigate(from || '/', { replace: true });
      } catch (e) {
        setError(e.response?.data?.error || 'Could not create your account. Please try again.');
        setSubmitting(false);
      }
    }
  });

  const onSubmit = (e) => {
    if (!isValidPhoneFor(phoneCountry, phone)) setPhoneError(phoneCountry === 'IN' ? 'Enter a valid 10-digit mobile number.' : 'Enter a valid mobile number.');
    handleSubmit(e);
  };

  return (
    <AuthShell
      title='Create account'
      subtitle='Save your address for faster checkout and track your orders.'
      footer={<Text color='blackAlpha.700' >Already have an account? <Link as={RouterLink} to='/login' state={{ from }} color='raano.black' fontWeight={700} textDecoration='underline' >Log in</Link></Text>}
    >
      <GoogleButton
        text='signup_with'
        onSuccess={(data) => {
          login(data.currentUser, data.token, true);
          toast({ title: data.created ? `Welcome to Raano, ${data.currentUser.firstName}!` : `Welcome back, ${data.currentUser.firstName}!`, status: 'success', duration: 1800, position: 'top' });
          navigate(data.currentUser.admin ? '/admin' : (from || '/'), { replace: true });
        }}
        onError={setError}
      />
      <Box as='form' onSubmit={onSubmit} noValidate >
        {error &&
          <Alert status='error' borderRadius={10} mb={4} fontSize={14} >
            <AlertIcon />
            <Box>
              {error}
              {/already exists/.test(error) && <Link as={RouterLink} to='/login' state={{ from }} ml={1} fontWeight={700} textDecoration='underline' >Log in instead</Link>}
            </Box>
          </Alert>}

        <SimpleGrid columns={2} spacing={3} mb={4} >
          <FormControl isInvalid={touched.firstName && !!errors.firstName} >
            <FormLabel fontSize={14} mb={1} >First name</FormLabel>
            <Input name='firstName' autoComplete='given-name' size='lg' fontSize={16} value={values.firstName} onChange={handleChange} onBlur={handleBlur} />
            <FormErrorMessage fontSize={12} >{errors.firstName}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={touched.lastName && !!errors.lastName} >
            <FormLabel fontSize={14} mb={1} >Last name</FormLabel>
            <Input name='lastName' autoComplete='family-name' size='lg' fontSize={16} value={values.lastName} onChange={handleChange} onBlur={handleBlur} />
            <FormErrorMessage fontSize={12} >{errors.lastName}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <FormControl isInvalid={touched.email && !!errors.email} mb={4} >
          <FormLabel fontSize={14} mb={1} >Email</FormLabel>
          <Input name='email' type='email' inputMode='email' autoComplete='email' autoCapitalize='none' placeholder='you@example.com' size='lg' fontSize={16} value={values.email} onChange={handleChange} onBlur={handleBlur} />
          <FormErrorMessage>{errors.email}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!phoneError} mb={4} >
          <FormLabel fontSize={14} mb={1} >Mobile number</FormLabel>
          <PhoneInput country={phoneCountry} number={phone} onChange={(country, number) => { setPhoneCountry(country); setPhone(number); setPhoneError(''); }} />
          <FormErrorMessage>{phoneError}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={touched.password && !!errors.password} mb={4} >
          <FormLabel fontSize={14} mb={1} >Password</FormLabel>
          <InputGroup size='lg' >
            <Input name='password' type={show ? 'text' : 'password'} autoComplete='new-password' placeholder='Create a password' fontSize={16} value={values.password} onChange={handleChange} onBlur={handleBlur} />
            <InputRightElement>
              <IconButton size='sm' variant='ghost' borderRadius='full' aria-label={show ? 'Hide password' : 'Show password'} icon={show ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />} onClick={() => setShow(!show)} />
            </InputRightElement>
          </InputGroup>
          <Box display='flex' gap={3} mt={2} flexWrap='wrap' >
            {PASSWORD_RULES.map(rule => {
              const ok = rule.test(values.password);
              return (
                <Box key={rule.label} display='flex' alignItems='center' gap={1} fontSize={12} color={ok ? 'green.600' : 'blackAlpha.600'} >
                  {ok ? <CheckCircle sx={{ fontSize: 14 }} /> : <RadioButtonUnchecked sx={{ fontSize: 14 }} />}{rule.label}
                </Box>
              );
            })}
          </Box>
        </FormControl>

        <FormControl isInvalid={touched.terms && !!errors.terms} mb={6} >
          <Checkbox name='terms' isChecked={values.terms} onChange={(e) => setFieldValue('terms', e.target.checked)} colorScheme='blackAlpha' alignItems='flex-start' >
            <Text fontSize={14} color='blackAlpha.700' >I agree to Raano's terms and privacy policy.</Text>
          </Checkbox>
          <FormErrorMessage>{errors.terms}</FormErrorMessage>
        </FormControl>

        <Button type='submit' width='100%' variant='ink' height='48px' fontSize='17px' isLoading={isSubmitting} loadingText='Creating account…' >Create account</Button>
      </Box>
    </AuthShell>
  )
}

export default Register;
